import { PartitionModel, DataSourceItem } from 'models/Enterprise/DataSourceModel';
import { useItemActionStates } from 'components/DiyWidgetEngine/common/hooks/useItemActionStates';
import { FormItemState } from 'models/Enterprise/FieldsManagerModel';

export interface PartitionStateModel {
  Select: FormItemState;
  ManageFields: FormItemState;
  Delete: FormItemState;
  Drag: FormItemState;
}

export interface PartitionsGlobalState {
  AddPartition: FormItemState;
}

export const DEFAULT_PARTITION_STATE: PartitionStateModel = {
  Select: { visible: true, disabled: false },
  ManageFields: { visible: true, disabled: false },
  Delete: { visible: true, disabled: false },
  Drag: { visible: true, disabled: false },
};

export const DEFAULT_PARTITIONS_GLOBAL_STATE: PartitionsGlobalState = {
  AddPartition: { visible: true, disabled: false },
};

/**
 * Computes the partition state for a single item.
 * Pure function — no closure state needed.
 */
const computePartitionState = (
  _item: DataSourceItem,
  totalItems: number,
  totalAvailable: number
): PartitionStateModel => {
  const isSingleSource = totalAvailable <= 1;
  const isSingleItem = totalItems <= 1;
  return {
    ...DEFAULT_PARTITION_STATE,
    Drag: { visible: !isSingleSource, disabled: isSingleItem },
    Delete: { visible: !isSingleSource, disabled: false },
  };
};

/**
 * Computes the global partitions state (e.g., AddPartition visibility).
 * Pure function — no closure state needed.
 */
const computeGlobalState = (
  items: DataSourceItem[],
  availableOptions: PartitionModel[]
): PartitionsGlobalState => {
  const totalAvailable = availableOptions.length;
  const selectedValues = items.map((item) => item.selectedValue?.value).filter(Boolean);
  const remainingOptions = availableOptions.filter(
    (option) => !selectedValues.includes(option.value)
  );
  return {
    AddPartition: {
      visible: totalAvailable > 1,
      disabled: remainingOptions.length === 0,
    },
  };
};

export interface UsePartitionStatesResult {
  globalState: PartitionsGlobalState;
  itemStates: Map<number, PartitionStateModel>;
}

/**
 * Custom hook to compute partition states for all items in the collection.
 * Memoized to prevent unnecessary recalculations.
 *
 * @param items - Array of data source items
 * @returns Global state and per-item state map
 */
export const usePartitionStates = (
  items: DataSourceItem[],
  availableOptions: PartitionModel[] = []
): UsePartitionStatesResult =>
  useItemActionStates(
    items,
    (item, _index, all) => computePartitionState(item, all.length, availableOptions.length),
    (all) => computeGlobalState(all, availableOptions),
    [availableOptions]
  );
